import { useQuery } from '@apollo/client'
import { Brand, getBrandByBrandName } from '@shared/domain/brand'
import { RemoteStoreSearchDocument } from '@shared/gql/document-nodes'
import { Button, InputSize, SearchInput, Tag, TagType } from '@sok/design-system'
import { PageTitle, StyledParagraph, TextBlock } from 'components/common/Titles'
import { NextLink } from 'components/NextLink'
import { SkeletonLoader } from 'components/SkeletonLoader'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import { TEXT_CONTENT_MAX_WIDTH } from 'styles/layout'
import { useDebouncedCallback } from 'use-debounce'
import { useCanonicalUrl } from 'utils/hooks/use-canonical-url'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'
import { useURLSearchParams } from 'utils/hooks/use-url-search-params'
import { ContentPageLayout } from 'views/contentful/components/ContentPageLayout'
import { StoreInfoCardSkeletonLoader } from 'views/store/components/StoreInfoCard'

import { addQueryWildcards } from './add-query-wildcards'
import { NoStoresError } from './components/NoStoresError'
import { StoreList } from './components/StoreList'
import { brands, storeBrandByBrand } from './store-brand'

const StyledPageContentContainer = styled.div(({ theme }) => ({
  backgroundColor: theme.color.white,
  width: '100%',
}))

const PageContent = styled.div(({ theme }) => ({
  padding: `${theme.spacings.xxLarge} ${theme.spacings.small}`,
  maxWidth: TEXT_CONTENT_MAX_WIDTH,
  margin: '0 auto',

  ['> * + *']: {
    marginTop: theme.spacings.large,
  },
}))

const BrandLinks = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacings.xSmall,
  justifyContent: 'center',

  ['> *']: {
    flex: '0 0 auto',
  },
}))

const CenteredContainer = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  rowGap: theme.spacings.medium,
  padding: `${theme.spacings.xxLarge} 0`,
}))

const REGEXP_SPACES = /\s/

const getSearchUrl = (brand: Brand | undefined, params: Record<string, string>) => {
  const queryString = getSearchQueryParamString(params)
  return `/myymalat${brand ? `/${brand}` : ''}${queryString}`
}

const getSearchQueryParamString = (params: Record<string, string>) => {
  const queryParams = new URLSearchParams(params)

  queryParams.forEach((value, key) => {
    if (value === '') {
      queryParams.delete(key)
    }
  })

  const queryParamString = queryParams.toString()
  return queryParamString ? `?${queryParamString}` : ''
}

const Loader = () => (
  <div>
    <StoreInfoCardSkeletonLoader />
    <StoreInfoCardSkeletonLoader />
    <StoreInfoCardSkeletonLoader />
    <StoreInfoCardSkeletonLoader />
  </div>
)

export const StoresPage = () => {
  const { t } = useTranslation()

  const router = useRouter()

  const brandParam = Array.isArray(router.query.brand)
    ? storeBrandByBrand(getBrandByBrandName(router.query.brand[0]) || Brand.Eprisma)
    : null

  const { params } = useURLSearchParams()
  const cursorQueryParam = params.get('cursor')
  const searchQueryParam = params.get('query')

  const [searchQuery, setSearchQuery] = useState<string | null>(searchQueryParam)

  const updateQuery = useDebouncedCallback(async (newSearchQuery: string) => {
    const query = { ...router.query }
    delete query.cursor

    if (newSearchQuery) {
      query.query = newSearchQuery
    } else {
      delete query.query
    }

    await router.push({ pathname: router.pathname, query })
  }, 500)

  useEffect(() => {
    setSearchQuery(searchQueryParam)
  }, [searchQueryParam])

  const hasQuery = brandParam || searchQueryParam

  const queryWithWildcards = useMemo(
    () => (searchQueryParam ? addQueryWildcards(searchQueryParam) : null),
    [searchQueryParam],
  )

  const { data, loading, fetchMore } = useQuery(RemoteStoreSearchDocument, {
    skip: !hasQuery, // don't fetch if no query or brand filters
    variables: {
      query: queryWithWildcards,
      brand: brandParam,
      cursor: cursorQueryParam || null,
    },
  })

  const stores = data?.searchStores?.stores

  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const handleLoadMoreClick = useCallback(async () => {
    setIsLoadingMore(true)
    await fetchMore({ variables: { cursor: data?.searchStores?.cursor } })
    setIsLoadingMore(false)
  }, [data?.searchStores?.cursor, fetchMore])

  const activeBrand = brands.find((brand) => storeBrandByBrand(brand) === brandParam)

  const pageTitle = useMemo(() => {
    const brandLabel = activeBrand ? t(activeBrand) : ''

    let context = brandLabel
    if (REGEXP_SPACES.test(brandLabel)) {
      context = 'withBrandWithSpaces'
    } else if (brandLabel) {
      context = 'withBrand'
    }

    return t('Store contact info', {
      context,
      brand: brandLabel,
    })
  }, [activeBrand, t])

  const theme = useTheme()
  const seoCanonical = useCanonicalUrl()
  const seoCanonicalQueryString = getSearchQueryParamString({
    cursor: cursorQueryParam ?? '',
  })
  const seoTitle = `${pageTitle} | ${theme.seoTags.title}`

  const isClientSide = useIsClientSide()

  return (
    <ContentPageLayout data-test-id="stores-page">
      <NextSeo title={seoTitle} canonical={`${seoCanonical}${seoCanonicalQueryString}`} />

      <StyledPageContentContainer>
        <PageContent>
          <TextBlock>
            <PageTitle>{pageTitle}</PageTitle>
            <StyledParagraph>{t('Store listings body')}</StyledParagraph>
          </TextBlock>

          {/* for unknown reason ssr fails with SearchInput component due to missing context */}
          {isClientSide ? (
            <SearchInput
              buttonLabel={t('Search')}
              size={InputSize.LARGE}
              value={searchQuery ?? ''}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                updateQuery(e.target.value)
              }}
              placeholder={t('Search by address or store name')}
              data-test-id="store-search"
            />
          ) : (
            <SkeletonLoader height={54} />
          )}

          <BrandLinks data-test-id="brand-tag-container">
            {brands.map((brand, i) => {
              const isActive = storeBrandByBrand(brand) === brandParam

              return (
                <NextLink
                  aria-label={isActive ? t('Clear store selection') : undefined}
                  href={getSearchUrl(isActive ? undefined : brand, {
                    query: searchQueryParam ?? '',
                  })}
                  shallow
                  key={i}
                  data-test-id="brand-tag"
                >
                  <Tag type={isActive ? TagType.DEFAULT : TagType.LIGHT}>{t(brand)}</Tag>
                </NextLink>
              )
            })}
          </BrandLinks>

          {hasQuery &&
            !loading &&
            (stores?.length ? (
              <StoreList stores={stores} />
            ) : (
              <NoStoresError query={searchQueryParam ?? ''} />
            ))}

          {(loading || isLoadingMore) && <Loader />}

          {data?.searchStores?.cursor && !isLoadingMore && (
            <CenteredContainer>
              <p>
                {t('Search results count', {
                  count: stores?.length,
                  totalCount: data?.searchStores?.totalCount,
                })}
              </p>

              <NextLink
                href={getSearchUrl(activeBrand, {
                  cursor: data.searchStores?.cursor,
                  query: searchQueryParam ?? '',
                })}
                shallow
              >
                <Button text={t('Show more')} onClick={handleLoadMoreClick} />
              </NextLink>
            </CenteredContainer>
          )}
        </PageContent>
      </StyledPageContentContainer>
    </ContentPageLayout>
  )
}
